// Port of TrailBivvy/App/Services/OfflineController.swift (iOS app): UI-facing state for offline packages ("Download
// this trek"), backed by the resumable download manager. Framework-free: the UI subscribes to state changes.
//
// Web deviations:
// - `lastError` is a machine-readable `OfflineControllerError` (no sentences outside src/i18n and src/ui); Swift held a
//   localized string.
// - `diskUsage` is the origin's usage from `navigator.storage.estimate()` (app shell, data and tiles), or the sum of the
//   regions' downloaded bytes when the browser does not tell.
// - `download` takes the route data package (pinned files, their byte size, the regulatory as-of date) instead of a
//   TrailDataset; size estimates of every level are computed one level per macrotask so the main thread stays
//   responsive while long corridors are enumerated (Swift: Task.detached).

import type { EpochMs, OfflinePackageLevel, OfflineRegion, TrailRoute } from '../core/models';
import { offlineLevelContents, offlineLevels } from '../core/domain';
import type { CompositeMapProvider } from '../map/CompositeMapProvider';
import {
  OfflineDownloadManager,
  OfflineError,
  type OfflineDataPackage,
  type OfflineDownloadEvent,
  type StorageManagerLike,
} from './OfflineDownloadManager';
import { OfflinePackagePlanner } from './OfflinePackagePlanner';
import type { OfflineStore } from './OfflineStore';

/** Why the last action failed (the UI localises it; Swift `OfflineController.describe`). */
export type OfflineControllerError =
  | { kind: 'insufficientStorage'; required: number; available: number }
  /** Route data saved; map tiles need an internet connection — try again when connected. */
  | { kind: 'offline' }
  | { kind: 'regionNotFound' }
  | { kind: 'cancelled' }
  /** Regulations can only be refreshed online; the saved copy stays available offline. */
  | { kind: 'regulationsNeedConnection' }
  /** Anything else: the download resumes where it stopped. */
  | { kind: 'generic' };

export interface OfflineControllerState {
  regions: OfflineRegion[];
  lastError?: OfflineControllerError;
  /** Bytes used on the device (see the module comment). */
  diskUsage: number;
}

/** Route data of a download plus its known size (catalogue `packBytes` + rules size) for the estimate. */
export type OfflineDownloadData = OfflineDataPackage & { dataBytes?: number };

export interface OfflineControllerOptions {
  store: OfflineStore;
  manager: OfflineDownloadManager;
  mapProvider: CompositeMapProvider;
  now?: () => EpochMs;
  storage?: StorageManagerLike;
}

/** Yields to the event loop (lets the UI paint between heavy planning steps). */
function nextMacrotask(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

export class OfflineController {
  private readonly store: OfflineStore;
  private readonly manager: OfflineDownloadManager;
  private readonly mapProvider: CompositeMapProvider;
  private readonly now: () => EpochMs;
  private readonly storage?: StorageManagerLike;
  private readonly listeners = new Set<(state: OfflineControllerState) => void>();
  private unsubscribeManager?: () => void;

  regions: OfflineRegion[] = [];
  lastError: OfflineControllerError | undefined;
  diskUsage = 0;

  /** Called after offline data changes (download completed/failed/deleted, rules refreshed). */
  onDatasetsChanged?: () => void;
  /** Called for `allData` packages to cache forecasts along the route. */
  prefetchWeather?: (route: TrailRoute) => Promise<void>;

  constructor(options: OfflineControllerOptions) {
    this.store = options.store;
    this.manager = options.manager;
    this.mapProvider = options.mapProvider;
    this.now = options.now ?? (() => Date.now());
    this.storage = options.storage ?? (typeof navigator === 'undefined' ? undefined : navigator.storage);
  }

  /** Current state snapshot. */
  get state(): OfflineControllerState {
    return { regions: this.regions, lastError: this.lastError, diskUsage: this.diskUsage };
  }

  /** Subscribes to state changes. Returns the unsubscribe function. */
  subscribe(listener: (state: OfflineControllerState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private changed(): void {
    const state = this.state;
    for (const listener of [...this.listeners]) listener(state);
  }

  /** Starts listening to the download manager and loads the regions. */
  start(): Promise<void> {
    if (!this.unsubscribeManager) this.unsubscribeManager = this.manager.subscribe((event) => void this.handle(event));
    return this.refresh();
  }

  /** Stops listening to the download manager. */
  stop(): void {
    this.unsubscribeManager?.();
    this.unsubscribeManager = undefined;
  }

  /** Reloads regions and the storage figure. */
  async refresh(): Promise<void> {
    this.regions = await this.store.regions();
    this.diskUsage = await this.measureUsage();
    this.changed();
  }

  /** Region identifier used for a route package at a level: "route:<routeID>:<level>". */
  static regionID(route: TrailRoute, level: OfflinePackageLevel): string {
    return `route:${route.id}:${level}`;
  }

  /**
   * Size estimate for each package level of a route (shown before downloading). Layers: every provider the route
   * crosses, each counted only inside its own coverage.
   */
  async estimates(route: TrailRoute): Promise<Record<OfflinePackageLevel, number>> {
    const result = {} as Record<OfflinePackageLevel, number>;
    for (const level of offlineLevels) {
      await nextMacrotask();
      result[level] = OfflinePackagePlanner.estimatedBytes(route, level, this.mapProvider.offlineTileLayersForRoute(level, route));
    }
    return result;
  }

  clearError(): void {
    this.lastError = undefined;
    this.changed();
  }

  /** The downloaded region covering a route, if any. */
  regionForRouteID(routeID: string): OfflineRegion | undefined {
    return this.regions.find((r) => r.id.startsWith(`route:${routeID}:`));
  }

  /** Starts (or restarts) a download of the route corridor at the chosen level. */
  async download(route: TrailRoute, level: OfflinePackageLevel, data: OfflineDownloadData): Promise<void> {
    this.lastError = undefined;
    this.changed();
    await nextMacrotask();
    // The layers are the union of every provider the route crosses (swisstopo in Switzerland, IGN in France…); each
    // carries its coverage, so the planner only requests a provider's tiles inside its own country.
    const plan = OfflinePackagePlanner.plan({
      route,
      regionID: OfflineController.regionID(route, level),
      name: route.name,
      level,
      layers: this.mapProvider.offlineTileLayersForRoute(level, route),
      dataBytes: data.dataBytes,
      now: this.now(),
    });
    try {
      await this.manager.start(plan, { urls: data.urls, regulationsAsOf: data.regulationsAsOf });
      if (offlineLevelContents(level).has('weather')) await this.prefetchWeather?.(route);
    } catch (error) {
      console.error('Download failed', error);
      this.lastError = OfflineController.describe(error);
    }
    await this.refresh();
    this.onDatasetsChanged?.();
  }

  async pause(region: OfflineRegion): Promise<void> {
    this.manager.pause(region.id);
    await this.refresh();
  }

  async resume(region: OfflineRegion): Promise<void> {
    try {
      await this.manager.resume(region.id);
    } catch (error) {
      this.lastError = OfflineController.describe(error);
    }
    await this.refresh();
  }

  /** Deletes the region, its tiles (unless another region needs them) and its pinned data. */
  async delete(region: OfflineRegion): Promise<void> {
    await this.manager.cancel(region.id);
    await this.refresh();
    this.onDatasetsChanged?.();
  }

  /**
   * Lightweight regulatory refresh, independent of map tiles: re-downloads the region's pinned files. `asOf` computes
   * the as-of date of the refreshed content from the downloaded JSON documents.
   */
  async refreshRegulations(region: OfflineRegion, asOf?: (documents: unknown[]) => EpochMs | undefined): Promise<void> {
    try {
      await this.manager.refreshRegulations(region.id, asOf);
    } catch (error) {
      this.lastError =
        error instanceof OfflineError && error.error.kind === 'offline'
          ? { kind: 'regulationsNeedConnection' }
          : OfflineController.describe(error);
    }
    await this.refresh();
    this.onDatasetsChanged?.();
  }

  /** `queued` or `downloading` (Swift `OfflineRegionStatus.isActive`). */
  isDownloading(region: OfflineRegion): boolean {
    return region.status === 'queued' || region.status === 'downloading';
  }

  private async handle(event: OfflineDownloadEvent): Promise<void> {
    switch (event.kind) {
      case 'progress': {
        const index = this.regions.findIndex((r) => r.id === event.regionID);
        if (index >= 0) {
          this.regions = this.regions.map((r, i) =>
            i === index
              ? { ...r, completedTiles: event.completedTiles, totalTiles: event.totalTiles, downloadedBytes: event.bytes, status: 'downloading' }
              : r,
          );
          this.changed();
        } else {
          await this.refresh();
        }
        break;
      }
      case 'stateChanged': {
        const index = this.regions.findIndex((r) => r.id === event.region.id);
        this.regions = index >= 0 ? this.regions.map((r, i) => (i === index ? event.region : r)) : [...this.regions, event.region];
        this.changed();
        break;
      }
      case 'completed':
      case 'failed':
        await this.refresh();
        this.onDatasetsChanged?.();
        break;
    }
  }

  private async measureUsage(): Promise<number> {
    try {
      const estimate = await this.storage?.estimate?.();
      if (estimate?.usage !== undefined) return estimate.usage;
    } catch {
      // fall through to the regions' own figures
    }
    return this.regions.reduce((sum, r) => sum + r.downloadedBytes, 0);
  }

  static describe(error: unknown): OfflineControllerError {
    if (error instanceof OfflineError) {
      switch (error.error.kind) {
        case 'insufficientStorage':
          return { kind: 'insufficientStorage', required: error.error.required, available: error.error.available };
        case 'offline':
          return { kind: 'offline' };
        case 'regionNotFound':
          return { kind: 'regionNotFound' };
        case 'cancelled':
          return { kind: 'cancelled' };
      }
    }
    return { kind: 'generic' };
  }
}
